import React, {
  createContext,
  useContext,
  useRef,
  SyntheticEvent,
  ChangeEvent,
  useCallback,
} from "react";
import {
  useInput,
  InputProps,
  FieldTitle,
  ResettableTextField,
  InputHelperText,
  useLocale,
  useTranslate,
} from "react-admin";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { makeStyles } from "@material-ui/core";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../../common/util/localization";
import { enUS } from "date-fns/locale";
import { zonedTimeToUtc } from "date-fns-tz";
import ProLogsDateFnsUtils from "../../../common/datetime/ProLogsDateFnsUtils";

interface Props extends InputProps {
  PickerComponent: React.FC<any>;
  options?: any;
  timeZone?: string | null;
}

interface IInputProps extends InputProps {
  id: string;
  onChange: (event: any) => void;
}

const isString = (x: any) => !!x.toUpperCase;

const InputPropsContext = createContext<IInputProps>({
  source: "",
  id: "",
  onChange: () => {},
});

const useStyles = makeStyles(
  (theme) => ({
    input: {
      minWidth: theme.spacing(20),
    },
  }),
  { name: "RaPickerInput" }
);

const ResettableTextFieldWithProps = ({
  defaultValue,
  onClick,
  ...props
}: any) => {
  const classes = useStyles();
  const context = useContext(InputPropsContext);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <ResettableTextField
      inputRef={inputRef}
      source={context.source}
      id={context.id}
      className={classes.input}
      onChange={(e: SyntheticEvent<ChangeEvent>) => {
        context.onChange(e);
      }}
      onClick={(e: SyntheticEvent<MouseEvent>) => {
        if (e.target === inputRef.current && onClick) {
          onClick(e);
        }
      }}
      {...filterProps(props)}
      resettable
    />
  );
};

export const GenericDateOrTimePicker = ({
  PickerComponent,
  className,
  options,
  disabled,
  timeZone,
  ...props
}: Props) => {
  const { variant, ...rest } = props;

  const {
    id,
    input: { name, onChange, onBlur, ...input },
    meta: { touched, error, submitError },
    isRequired,
  } = useInput(rest);

  const handleChange = useCallback(
    (value) => {
      let dateTime: Date;
      if (isString(value)) {
        dateTime = new Date(Date.parse(value));
      } else {
        dateTime = value;
      }

      if (dateTime) {
        onChange(
          zonedTimeToUtc(
            dateTime,
            timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
          )
        );
        return;
      }

      onChange(null);
    },
    [onChange, timeZone]
  );

  const formatValue = useCallback(
    (value) => {
      const dateTimeString = value;
      if (dateTimeString) {
        const originalDate = new Date(dateTimeString);
        if (!timeZone) {
          return originalDate;
        }

        const parsedDate = new Date(
          Date.parse(originalDate.toLocaleString("en-US", { timeZone }))
        );

        return parsedDate;
      }

      return null;
    },
    [timeZone]
  );

  const translate = useTranslate();
  const locale = useLocale() as ProLogsLocale;
  return (
    <MuiPickersUtilsProvider
      utils={ProLogsDateFnsUtils}
      locale={!!dateFnsLocaleMap[locale] ? dateFnsLocaleMap[locale] : enUS}
    >
      <InputPropsContext.Provider
        value={{ source: props.source, id, onChange }}
      >
        <PickerComponent
          disabled={disabled}
          className={className}
          name={name}
          label={
            props.label !== "" &&
            props.label !== false && (
              <FieldTitle
                label={props.label}
                source={props.source}
                resource={props.resource}
                isRequired={props.isRequired}
              />
            )
          }
          onChange={(value: any) => handleChange(value)}
          error={!!(error || submitError)}
          helperText={
            <InputHelperText
              touched={!!(touched || error || submitError)}
              error={error || submitError}
              helperText={props.helperText}
            />
          }
          required={isRequired}
          inputVariant={variant}
          TextFieldComponent={ResettableTextFieldWithProps}
          {...input}
          {...options}
          okLabel={translate("ra.action.confirm")}
          cancelLabel={translate("ra.action.cancel")}
          value={formatValue(input.value)}
          timeZone={timeZone}
        />
      </InputPropsContext.Provider>
    </MuiPickersUtilsProvider>
  );
};

const filterProps = (props: any) => {
  const { timeZone, ...rest } = props;
  return rest;
};